/**
 * client/src/lib/error-copy.ts
 *
 * Single source of truth for the GENERIC fallback line a call site shows when
 * it has already checked every cause-specific error code it knows about and
 * none matched (#3386). Every other file that threw a bare "Something went
 * wrong. Please try again." — or an equivalent unbranded string — regardless
 * of WHY the request failed, so a rate-limited retry, a dead network, a 500
 * and a genuinely unknown failure all read identically to the user, none of
 * them saying what to do next.
 *
 * "Words are UX": every message here answers "what do I do now?" and ends in
 * an action verb (DESIGN-SYSTEM.md §7.1 rule 5) — never a bare "Failed" or
 * "Something went wrong", with ONE deliberate exception: the 423 Forensic
 * Pause branch never says "try again" (a frozen account is not a retry
 * state — CLAUDE.md §Forensic Pause). Plain language only, no jargon (§7.1
 * rule 1). This module never adds red styling — that stays the caller's
 * decision (red is reserved for Forensic Pause + critical OSINT only).
 *
 * COPY / PRESENTATION ONLY. This module never decides retry policy, never
 * classifies an error as terminal vs transient for CONTROL-FLOW purposes
 * (that stays local to each call site, e.g. `recoveryInitiateErrorCopy` /
 * `initiate-error-map.ts`), and never replaces a cause-specific mapping a
 * call site already has — it exists for the one branch every such mapping
 * falls through to.
 *
 * Deliberately does NOT import `ApiError` from `@/lib/queryClient` — that
 * module instantiates the app's singleton `QueryClient` at import time, which
 * several unit tests `vi.mock("@tanstack/react-query")` without a `QueryClient`
 * export (they only stub `useQuery`/`useQueryClient`). Pulling that import in
 * transitively broke every one of those tests the moment they imported this
 * file. `status` is read structurally instead — `ApiError` already exposes a
 * public `status: number` field, so this recognises a real `ApiError` the
 * same way, with no import needed.
 */

import { isChunkLoadError } from "@/lib/chunk-reload";
import { FORENSIC_PAUSE_GENERIC_MESSAGE } from "@/lib/terminology";

export interface DescribeErrorOptions {
  /** Lower-case verb phrase completing "We couldn't ___", e.g. "remove this key". */
  action: string;
  /**
   * True for a WRITE (non-idempotent) call site. A network failure there
   * cannot tell the caller whether the request landed before the connection
   * dropped, so the honest copy is different from a READ's plain "check your
   * connection" — precedent: `use-cancellation-ceremony.ts`'s
   * `confirmCancellationErrorMessage`. Applies only to the two NETWORK-DOWN
   * branches (offline, and a network-shaped `TypeError`) — a response that
   * DID arrive (429/5xx/423/unknown) already answers "did it go through"
   * (no, the server said so), so those keep their normal copy regardless.
   */
  outcomeUnknownOnNetworkError?: boolean;
  /**
   * Override the trailing action on the UNKNOWN-cause branch only (every
   * other branch already names its own accurate next step — offline says
   * check the connection, chunk-load says reload, 423 says nothing-to-retry,
   * 429/5xx say when to retry). For a screen whose only real control is a
   * reload (App.tsx's root boundary, the default `ErrorBoundary` fallback),
   * pass e.g. "Reload the page." so the headline names the actual button
   * instead of a generic "Try again" the surrounding copy would then repeat.
   */
  nextStep?: string;
  /**
   * Keep the server's own sentence for a CODED 4xx refusal (not 423/429).
   * Server refusals that carry a `code` (GuardianError, ladder-floor guidance,
   * quorum holds, rate-limit windows) are written as user copy and say why the
   * action was refused — collapsing them to "Try again" sends the user round a
   * loop that can't succeed (DESIGN-SYSTEM §7.1 rule 5). Off by default: most
   * call sites map their known codes first and want the generic line after.
   */
  keepServerMessage?: boolean;
}

/** Exact browser/runtime message shapes `fetch()` throws as a bare `TypeError`
 *  when the request never reached a server (no response, so `apiRequest`
 *  never wraps it in `ApiError`) — Chrome/Edge/Node, Firefox, Safari, and
 *  React Native's fetch polyfill respectively. Anchored (`^…$`) so a chunk-
 *  load rejection's much longer message (checked first, see `describeError`)
 *  never accidentally matches here too. */
const NETWORK_FAILURE_MESSAGE_PATTERN =
  /^(Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed|Network request failed)$/;

/** Shared wording for a confirmed network-layer failure (not chunk-load, not
 *  a browser-reported offline state) — reused verbatim by
 *  `step-up-error-copy.ts`, `api/action-policy.ts`,
 *  `security-centre/master/use-master-ceremony.ts` and
 *  `use-operator-grant-ceremony.ts` rather than re-typing the literal a fifth
 *  time. */
export const NETWORK_PROBLEM_MESSAGE = "Network problem. Check your connection and try again.";

/** The wait-then-retry line for a write whose network outcome is unknown
 *  (see `DescribeErrorOptions.outcomeUnknownOnNetworkError`). Deliberately
 *  does not say "try again" as its first instruction — checking is. */
const OUTCOME_UNKNOWN_NETWORK_MESSAGE =
  "We couldn't tell whether this went through. Refresh to check before trying again.";

function messageOf(err: unknown): string | undefined {
  return err instanceof Error ? err.message : undefined;
}

/** The HTTP status carried by an `ApiError` (read structurally — see the
 *  header note on why this file avoids importing the class), or by anything
 *  else shaped like one (a plain `{ status }` object from a raw `fetch`
 *  caller that hasn't wrapped its response in `ApiError`). */
function statusOf(err: unknown): number | undefined {
  if (typeof err === "object" && err !== null && "status" in err) {
    const status = (err as { status?: unknown }).status;
    if (typeof status === "number") return status;
  }
  return undefined;
}

/**
 * The generic fallback line for `err`, once every cause-specific code a call
 * site knows about has already been checked and ruled out. Classification
 * order (each one checked only after the ones above it are ruled out):
 *
 *   1. browser-reported offline (`navigator.onLine === false`)
 *   2. a stale lazy-loaded chunk failing to import after a deploy
 *      (`isChunkLoadError`, reused from `chunk-reload.ts`) -> reload copy
 *   3. a network-shaped `TypeError` (no response ever arrived)
 *   4. a 423 (Forensic Pause) -> the freeze sentence, never "try again"
 *   5. a 429 -> wait-then-retry copy
 *   6. a 5xx -> "Our server hit a problem. Try again in a minute."
 *   7. anything else, INCLUDING any other `TypeError` (e.g. a render bug
 *      like "Cannot read properties of undefined") -> the action fallback
 *
 * Steps 1 and 3 are "the network is down" in two different disguises — a
 * genuine render/logic bug that happens to throw `TypeError` (step 7) is NOT
 * one of them, which is the fix for the earlier "any TypeError reads as
 * offline" misdiagnosis: only a message shape browsers actually use for a
 * failed `fetch()` counts.
 *
 * `opts.action` names what the user was trying to do, in the fewest plain
 * words ("remove this key", "send this invite") — never a jargon term, never
 * the raw error code.
 */
export function describeError(err: unknown, opts: DescribeErrorOptions): string {
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const message = messageOf(err);

  if (!offline && isChunkLoadError(message)) {
    return "A new version is available. Reload the page to get it.";
  }

  const networkFailure = err instanceof TypeError && NETWORK_FAILURE_MESSAGE_PATTERN.test(message ?? "");
  if (offline || networkFailure) {
    if (opts.outcomeUnknownOnNetworkError) return OUTCOME_UNKNOWN_NETWORK_MESSAGE;
    return offline
      ? "You're offline. Check your connection, then try again."
      : NETWORK_PROBLEM_MESSAGE;
  }

  const status = statusOf(err);
  if (status === 423) {
    return FORENSIC_PAUSE_GENERIC_MESSAGE;
  }
  if (status === 429) {
    return "You're doing that too fast. Wait a minute, then try again.";
  }
  if (status !== undefined && status >= 500) {
    return "Our server hit a problem. Try again in a minute.";
  }
  const code = typeof err === "object" && err !== null ? (err as { code?: unknown }).code : undefined;
  if (opts.keepServerMessage && message && typeof code === "string" && code !== "" && status !== undefined && status >= 400) {
    return message;
  }
  return `We couldn't ${opts.action}. ${opts.nextStep ?? "Try again."}`;
}

